import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Check, X, Users, DollarSign, Clock, Wallet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function TenantAffiliates() {
  const [stats, setStats] = useState({});
  const [affiliates, setAffiliates] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processDialog, setProcessDialog] = useState({ open: false, withdrawal: null, action: '' });
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchData = async () => {
    try {
      const [statsRes, affRes, commRes, wdRes] = await Promise.all([
        api.get('/tenant/affiliate-stats'),
        api.get('/tenant/affiliates'),
        api.get('/tenant/affiliate-commissions'),
        api.get('/tenant/affiliate-withdrawals')
      ]);
      setStats(statsRes.data);
      setAffiliates(affRes.data.affiliates);
      setCommissions(commRes.data.commissions);
      setWithdrawals(wdRes.data.withdrawals);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/tenant/affiliates/${id}`, { status });
    toast.success(`Affiliate ${status === 'approved' ? 'disetujui' : 'ditolak'}`);
    fetchData();
  };

  const approveCommission = async (id) => {
    await api.put(`/tenant/affiliate-commissions/${id}/approve`);
    toast.success('Komisi disetujui');
    fetchData();
  };

  const handleProcessWithdrawal = async () => {
    if (!processDialog.withdrawal) return;
    setProcessing(true);
    try {
      await api.put(`/tenant/affiliate-withdrawals/${processDialog.withdrawal.id}`, {
        status: processDialog.action,
        notes: notes
      });
      toast.success(`Withdrawal ${processDialog.action === 'approved' ? 'disetujui' : 'ditolak'}`);
      setProcessDialog({ open: false, withdrawal: null, action: '' });
      setNotes('');
      fetchData();
    } catch (e) {
      toast.error('Gagal memproses withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" />
      </div>
    );
  }

  return (
    <div data-testid="tenant-affiliates-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Affiliate Program</h1>
        <p className="text-sm text-slate-500">Kelola affiliate dan komisi</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_affiliates || 0}</p>
                <p className="text-xs text-slate-500">Total Affiliate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <DollarSign size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">Rp {fmt(stats.total_commission)}</p>
                <p className="text-xs text-slate-500">Total Komisi</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">Rp {fmt(stats.pending_commission)}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Wallet size={20} className="text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">Rp {fmt(stats.pending_withdrawals)}</p>
                <p className="text-xs text-slate-500">Withdrawal Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="affiliates">
        <TabsList>
          <TabsTrigger value="affiliates">Affiliates ({affiliates.length})</TabsTrigger>
          <TabsTrigger value="commissions">Komisi ({commissions.length})</TabsTrigger>
          <TabsTrigger value="withdrawals">Withdrawals ({withdrawals.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="affiliates" className="mt-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Affiliate</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Code</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Rate</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Balance</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                    <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {affiliates.map(a => (
                    <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{a.name}</p>
                        <p className="text-xs text-slate-500">{a.email}</p>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs bg-slate-100 rounded">{a.code}</td>
                      <td className="py-3 px-4">{a.commission_rate || 10}%</td>
                      <td className="py-3 px-4 font-medium text-emerald-600">Rp {fmt(a.balance || 0)}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={
                          a.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 
                          a.status === 'pending' ? 'bg-amber-100 text-amber-700' : 
                          'bg-red-100 text-red-700'
                        }>{a.status}</Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {a.status === 'pending' && (
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => updateStatus(a.id, 'approved')} data-testid={`approve-affiliate-${a.id}`}>
                              <Check size={14} className="text-emerald-500" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => updateStatus(a.id, 'rejected')} data-testid={`reject-affiliate-${a.id}`}>
                              <X size={14} className="text-red-500" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!affiliates.length && (
                    <tr><td colSpan={6} className="py-12 text-center text-slate-400">Belum ada affiliate terdaftar</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="commissions" className="mt-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Tanggal</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Order</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Amount</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                    <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map(c => (
                    <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4 text-slate-600">{new Date(c.created_at).toLocaleDateString('id-ID')}</td>
                      <td className="py-3 px-4 font-mono text-xs">{c.order_id?.slice(0, 8)}...</td>
                      <td className="py-3 px-4 font-semibold text-emerald-600">Rp {fmt(c.amount)}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={
                          c.status === 'paid' ? 'bg-blue-100 text-blue-700' :
                          c.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 
                          'bg-amber-100 text-amber-700'
                        }>{c.status}</Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {c.status === 'pending' && (
                          <Button size="sm" variant="outline" onClick={() => approveCommission(c.id)}>Approve</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!commissions.length && (
                    <tr><td colSpan={5} className="py-12 text-center text-slate-400">Belum ada komisi</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="withdrawals" className="mt-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Affiliate</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Amount</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Bank</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                    <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map(w => (
                    <tr key={w.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{w.affiliate?.name}</p>
                        <p className="text-xs text-slate-500">{w.affiliate?.code}</p>
                      </td>
                      <td className="py-3 px-4 font-semibold text-emerald-600">Rp {fmt(w.amount)}</td>
                      <td className="py-3 px-4">
                        <p className="text-slate-900">{w.bank_name}</p>
                        <p className="text-xs text-slate-500">{w.account_number}</p>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={
                          w.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          w.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }>{w.status}</Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {w.status === 'pending' && (
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => setProcessDialog({ open: true, withdrawal: w, action: 'approved' })}>
                              <Check size={14} className="mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => setProcessDialog({ open: true, withdrawal: w, action: 'rejected' })}>
                              <X size={14} className="mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!withdrawals.length && (
                    <tr><td colSpan={5} className="py-12 text-center text-slate-400">Belum ada withdrawal request</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Process Withdrawal Dialog */}
      <Dialog open={processDialog.open} onOpenChange={(open) => !open && setProcessDialog({ open: false, withdrawal: null, action: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {processDialog.action === 'approved' ? 'Setujui' : 'Tolak'} Withdrawal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Affiliate</p>
              <p className="font-medium">{processDialog.withdrawal?.affiliate?.name}</p>
              <p className="text-sm text-slate-500 mt-2">Amount</p>
              <p className="font-semibold text-emerald-600">Rp {fmt(processDialog.withdrawal?.amount)}</p>
            </div>
            <div>
              <label className="text-sm font-medium">Catatan (Opsional)</label>
              <Textarea 
                value={notes} 
                onChange={(e) => setNotes(e.target.value)} 
                placeholder="Tambahkan catatan..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProcessDialog({ open: false, withdrawal: null, action: '' })}>Batal</Button>
            <Button 
              onClick={handleProcessWithdrawal} 
              disabled={processing}
              className={processDialog.action === 'approved' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'}
            >
              {processing && <Loader2 size={14} className="animate-spin mr-2" />}
              {processDialog.action === 'approved' ? 'Setujui' : 'Tolak'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
